import { useEffect, useState } from "react";
import { HOURS, CLOSED_DAYS, WEEK, CLINIC } from "./content";

/** Sets the browser tab title and meta description for a website page. */
export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title ? `${title} | ${CLINIC.name}, Khopoli` : `${CLINIC.name}, Khopoli | ${CLINIC.doctor}`;
    if (description) {
      const tag = document.querySelector('meta[name="description"]');
      if (tag) tag.setAttribute("content", description);
    }
  }, [title, description]);
}

/** Current day-of-week and minutes-since-midnight in India time. */
export function istNow() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", weekday: "short", hour: "numeric", minute: "numeric", hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return { day, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

const fmt = (mins) => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
};

/** "Open now · until 2:00 PM" / "Closed · opens 5:00 PM" etc. */
export function clinicStatus(now = istNow()) {
  const { day, minutes } = now;
  if (!CLOSED_DAYS.includes(day)) {
    const current = HOURS.find((h) => minutes >= h.from && minutes < h.to);
    if (current) return { open: true, text: "Open now", detail: `until ${fmt(current.to)}` };
    const later = HOURS.find((h) => minutes < h.from);
    if (later) return { open: false, text: "Closed now", detail: `opens today at ${fmt(later.from)}` };
  }
  // Next open day
  for (let i = 1; i <= 7; i += 1) {
    const d = (day + i) % 7;
    if (!CLOSED_DAYS.includes(d)) {
      const when = i === 1 ? "tomorrow" : WEEK[d];
      return { open: false, text: CLOSED_DAYS.includes(day) ? "Closed on Sundays" : "Closed now", detail: `opens ${when} at ${fmt(HOURS[0].from)}` };
    }
  }
  return { open: false, text: "Closed", detail: "" };
}

/** Live clinic status, refreshed every minute. */
export function useClinicStatus() {
  const [status, setStatus] = useState(() => clinicStatus());
  useEffect(() => {
    const id = setInterval(() => setStatus(clinicStatus()), 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return status;
}
